import React, { useState } from "react";
import { ArrowUp, ArrowDown, Plus, Trash2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { errText } from "../biz/i18n";
import { btnMain, btnSoft, inp } from "../biz/ui";
import { useLabT } from "./i18n";
import { Check } from "./fields";

const move = (arr, i, d) => { const a = [...arr]; const j = i + d; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; };
const Arrows = ({ onUp, onDown, testid }) => (
  <span className="flex gap-1">
    <button type="button" onClick={onUp} className="p-1.5 rounded-lg border border-[#E4DBCD] bg-white" data-testid={`${testid}-up`}><ArrowUp size={14} /></button>
    <button type="button" onClick={onDown} className="p-1.5 rounded-lg border border-[#E4DBCD] bg-white" data-testid={`${testid}-down`}><ArrowDown size={14} /></button>
  </span>
);

const QuestionEditor = ({ q, i, t, withStage, upd, del, up, down }) => {
  const [open, setOpen] = useState(false);
  const opts = q.options || [];
  const setOpt = (j, patch) => upd({ options: opts.map((o, k) => (k === j ? { ...o, ...patch } : o)) });
  return (
    <div className={`border rounded-2xl bg-white ${q.enabled ? "border-[#EEE5D8]" : "border-dashed border-[#D8CFC2] opacity-70"}`} data-testid={`qb-q-${i}`}>
      <div className="flex items-center gap-2 p-3">
        <button type="button" onClick={() => setOpen(!open)} className="flex-1 text-start text-sm min-w-0" data-testid={`qb-q-toggle-${i}`}>
          <span className="text-[#9A7445] me-1.5">{i + 1}.</span>{q.label_ar || q.label_en || "…"}
          <span className="block text-xs text-[#8A8173] truncate" dir="ltr">{q.label_en}</span>
        </button>
        <Arrows onUp={up} onDown={down} testid={`qb-q-${i}`} />
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      {open && (
        <div className="border-t border-[#EEE5D8] p-3 space-y-3">
          <input dir="rtl" value={q.label_ar} onChange={(e) => upd({ label_ar: e.target.value })} className={inp} placeholder="السؤال بالعربية" data-testid={`qb-q-ar-${i}`} />
          <input dir="ltr" value={q.label_en} onChange={(e) => upd({ label_en: e.target.value })} className={inp} placeholder="Question in English" data-testid={`qb-q-en-${i}`} />
          <div className="grid grid-cols-2 gap-2">
            <select value={q.type} onChange={(e) => upd({ type: e.target.value })} className={inp} data-testid={`qb-q-type-${i}`}>
              <option value="single">{t("t_single")}</option><option value="multi">{t("t_multi")}</option><option value="text">{t("t_text")}</option>
            </select>
            {withStage && <select value={q.stage} onChange={(e) => upd({ stage: Number(e.target.value) })} className={inp} data-testid={`qb-q-stage-${i}`}>
              <option value={1}>{t("first_impression")}</option><option value={2}>{t("full_evaluation")}</option></select>}
          </div>
          {q.type !== "text" && (
            <div className="space-y-2">
              {opts.map((o, j) => (
                <div key={o.id || j} className="flex gap-2 items-center" data-testid={`qb-o-${i}-${j}`}>
                  <span className="text-xs text-[#8A8173] w-5">{String.fromCharCode(65 + j)}</span>
                  <div className="flex-1 min-w-0 grid sm:grid-cols-2 gap-1.5">
                    <input dir="rtl" value={o.ar} onChange={(e) => setOpt(j, { ar: e.target.value })} className={`${inp} !py-2`} placeholder="عربي" data-testid={`qb-o-ar-${i}-${j}`} />
                    <input dir="ltr" value={o.en} onChange={(e) => setOpt(j, { en: e.target.value })} className={`${inp} !py-2`} placeholder="English" data-testid={`qb-o-en-${i}-${j}`} />
                  </div>
                  <Arrows onUp={() => upd({ options: move(opts, j, -1) })} onDown={() => upd({ options: move(opts, j, 1) })} testid={`qb-o-${i}-${j}`} />
                  <button type="button" onClick={() => upd({ options: opts.filter((_, k) => k !== j) })} className="p-1.5 text-[#A33A2B]" data-testid={`qb-o-del-${i}-${j}`}><Trash2 size={15} /></button>
                </div>
              ))}
              <button type="button" onClick={() => upd({ options: [...opts, { id: `o${Date.now().toString(36)}`, en: "", ar: "" }] })} className={btnSoft} data-testid={`qb-o-add-${i}`}><Plus size={14} />{t("add_option")}</button>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-2 pt-1">
            <Check label={t("required_q")} checked={q.required} onChange={(v) => upd({ required: v })} testid={`qb-q-req-${i}`} />
            <Check label={t("enabled")} checked={q.enabled} onChange={(v) => upd({ enabled: v })} testid={`qb-q-enabled-${i}`} />
            {q.type !== "text" && <Check label={t("allow_comment")} checked={q.comment} onChange={(v) => upd({ comment: v })} testid={`qb-q-comment-${i}`} />}
            {withStage && <Check label={t("skin_only")} checked={q.skin_only} onChange={(v) => upd({ skin_only: v })} testid={`qb-q-skin-${i}`} />}
          </div>
          <button type="button" onClick={() => window.confirm(t("confirm_delete")) && del()} className={`${btnSoft} !text-[#A33A2B]`} data-testid={`qb-q-del-${i}`}><Trash2 size={14} />{t("remove")}</button>
        </div>
      )}
    </div>
  );
};

export function QuestionBuilder({ field, initial, withStage, reload, testid }) {
  const { t } = useLabT();
  const [qs, setQs] = useState(initial || []);
  const [busy, setBusy] = useState(false);
  const upd = (i, patch) => setQs(qs.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const add = () => setQs([...qs, { id: `q_${Date.now().toString(36)}`, type: "single", label_en: "", label_ar: "", required: false, enabled: true, comment: true, stage: 1, skin_only: false,
    options: [1, 2, 3, 4].map((n) => ({ id: `o${n}`, en: "", ar: "" })) }]);
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/lab/settings", { [field]: qs }); toast.success(t("saved")); reload(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid={testid}>
      {qs.map((q, i) => <QuestionEditor key={q.id} q={q} i={i} t={t} withStage={withStage} upd={(p) => upd(i, p)} del={() => setQs(qs.filter((_, j) => j !== i))}
        up={() => setQs(move(qs, i, -1))} down={() => setQs(move(qs, i, 1))} />)}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={add} className={btnSoft} data-testid={`${testid}-add`}><Plus size={14} />{t("add_question")}</button>
        <button type="button" onClick={save} disabled={busy} className={btnMain} data-testid={`${testid}-save`}>{t("save")}</button>
      </div>
    </div>
  );
}

export const Collapse = ({ title, children, testid, defaultOpen = false }) => (
  <details open={defaultOpen} className="group bg-white border border-[#EEE5D8] rounded-2xl" data-testid={testid}>
    <summary className="list-none cursor-pointer flex items-center justify-between gap-3 px-4 py-3.5 font-semibold text-sm">
      {title}<ChevronDown size={16} className="transition-transform group-open:rotate-180" />
    </summary>
    <div className="px-4 pb-4">{children}</div>
  </details>
);
